"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import dynamic from "next/dynamic";
import { useSearchParams } from "next/navigation";
import { useAnalytics, useAudienceAnalytics, AudienceAnalyticsProvider } from "@/lib/analytics";
import type { CreatorProfileForMyLink } from "@artha/types";
import type {
  JobFiltersResponseDto,
  JobsResponseDto,
} from "@artha/api-contracts";
import { authService } from "@/lib/authService";
import { PageLayout } from "@/components/layout/PageLayout";
import { DEFAULT_COLORS } from "@/modules/creator/constants";
import {
  VpnRestrictionModal,
  useApplyRestrictions,
} from "@/modules/geolocation";
import {
  formatSalary,
  normalizeUsernameParam,
  shouldHideSalaryBadge,
  generateCreatorInviteUrl
} from "@/lib/utils";

// Islands
const ProfileHeader = dynamic(() => import("./components/ProfileHeader").then(m => m.ProfileHeader));
const PublicJobFeed = dynamic(() => import("./components/PublicJobFeed").then(m => m.PublicJobFeed));

type DesignColors = {
  pageBackground: string;
  profileCard: string;
  titleColor: string;
  ctaColor: string;
  textColor: string;
};

const DESIGN_COLOR_KEYS = [
  "pageBackground",
  "profileCard",
  "titleColor",
  "ctaColor",
  "textColor",
] as const;

type ColorKey = (typeof DESIGN_COLOR_KEYS)[number];

function isDesignUpdateMessage(data: any): data is { type: "DESIGN_UPDATE"; colors: Partial<DesignColors> } {
  if (data?.type !== "DESIGN_UPDATE") return false;
  if (!data.colors || typeof data.colors !== "object") return false;
  return Object.keys(data.colors).every((key) =>
    DESIGN_COLOR_KEYS.includes(key as ColorKey)
  );
}

type ProfilePageClientProps = {
    initialJobs: JobsResponseDto;
    username: string;
    creator: CreatorProfileForMyLink;
    filters?: JobFiltersResponseDto | null;
  };

function ProfilePageClientInner({
  initialJobs,
  username,
  creator,
  filters,
}: ProfilePageClientProps) {
  const searchParams = useSearchParams();
  const isPreviewMode = searchParams.get("preview") === "true";
  const { trackAudEarnClick, trackSocialMediaClick } = useAudienceAnalytics();
  const { trackPage } = useAnalytics();

  const jobsTopRef = useRef<HTMLDivElement | null>(null);
  const hasTrackedPageViewRef = useRef(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const checkAuth = () => {
      setIsAuthenticated(authService.isAuthenticated());
    };
    checkAuth();

    window.addEventListener("auth-change", checkAuth);
    return () => window.removeEventListener("auth-change", checkAuth);
  }, []);

  const savedColors = useMemo<DesignColors>(
    () => ({
      pageBackground: creator.appearance?.backgroundColor || DEFAULT_COLORS.pageBackground,
      profileCard: creator.appearance?.cardColor || DEFAULT_COLORS.profileCard,
      titleColor: creator.appearance?.titleColor || DEFAULT_COLORS.titleColor,
      ctaColor: creator.appearance?.ctaColor || DEFAULT_COLORS.ctaColor,
      textColor: creator.appearance?.textColor || DEFAULT_COLORS.textColor,
    }),
    [creator.appearance],
  );

  const [previewColors, setPreviewColors] = useState<DesignColors>(savedColors);

  useEffect(() => {
    if (!isPreviewMode) return;

    const handleMessage = (event: MessageEvent) => {
      if (
        event.origin !== window.location.origin ||
        event.source !== window.parent ||
        !isDesignUpdateMessage(event.data)
      ) {
        return;
      }

      setPreviewColors((prev) => ({ ...prev, ...event.data.colors }));
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [isPreviewMode]);

  const colors = isPreviewMode ? previewColors : savedColors;
  const normalizedUsername = useMemo(() => normalizeUsernameParam(username), [username]);
  const { isVpnModalOpen, closeVpnModal } = useApplyRestrictions({
    job: null,
    creatorUsername: normalizedUsername,
    creator,
  });

  useEffect(() => {
    if (hasTrackedPageViewRef.current) return;
    hasTrackedPageViewRef.current = true;
    trackPage("creator_profile", {
      creator_id: creator.id,
      creator_username: normalizedUsername,
      page_type: "creator_profile",
    });
  }, [creator.id, trackPage, normalizedUsername]);

  return (
    <PageLayout
      className="pb-16 relative"
      style={{ backgroundColor: colors.pageBackground }}
    >
      <main className="creator-profile-page flex flex-col items-center space-y-5 px-4 pt-11 relative">
        <ProfileHeader
          creator={creator}
          username={username}
          colors={colors}
          onSocialClick={trackSocialMediaClick}
        />

        <PublicJobFeed
          initialJobs={initialJobs}
          username={username}
          creator={creator}
          filters={filters}
          isAuthenticated={isAuthenticated}
          ctaColor={colors.ctaColor}
          jobsTopRef={jobsTopRef}
          isPreviewMode={isPreviewMode}
          onEarnClick={() => trackAudEarnClick(normalizedUsername)}
        />
      </main>
      <VpnRestrictionModal isOpen={isVpnModalOpen} onClose={closeVpnModal} />
    </PageLayout >
  );
}

export default function ProfilePageClient(props: ProfilePageClientProps) {
  return (
    <AudienceAnalyticsProvider username={props.username}>
      <ProfilePageClientInner {...props} />
    </AudienceAnalyticsProvider>
  );
}
